<script>
    /* States */
    import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
    import { explorerState } from "@app/store/explorerStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    /* Firebase */
    import { auth } from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import { collection, doc, getDoc, getDocs, query, orderBy, limit } from "firebase/firestore";
    /* Components */
    import NavSidebar from "./explorer/NavSidebar.svelte";
    import Workspace from "./explorer/Workspace.svelte";
    import InfoSidebar from "./explorer/InfoSidebar.svelte";
    import * as m from '@app/paraglide/messages.js';
    /** @type {{recent?: boolean}} */
    let { recent = $bindable(false) } = $props();

    let userId = auth.currentUser.uid;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = collection(db, "users", userId, "storage");
    let teamDbRef = teamId ? collection(db, "teams", teamId, "storage") : null;
    let operationLoading = false;

    // Helper function to check if a folder should be visible to current user based on group permissions
    function canUserAccessFolder(folder) {
        // Admin and Manager roles always have access to all folders
        if (authState.user && (authState.user.role === "admin" || authState.user.role === "manager")) {
            return true;
        }
        
        // If folder has no groups assigned, it's accessible to all team members
        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }
        
        // Check if user is a member of any of the folder's assigned groups
        if (authState.user && authState.user.groups) {
            return folder.groups.some(groupId => authState.user.groups.includes(groupId));
        }
        
        // If user has no groups, they can't access group-restricted folders
        return false;
    }

    // Helper function to check if a project record should be visible based on its parent folder permissions
    async function canUserAccessRecord(record) {
        // Personal records are always accessible
        if (record.rootDir === "personal") {
            return true;
        }
        
        // If record has no locationId, it's at root level and accessible
        if (!record.locationId) {
            return true;
        }
        
        // For team records with locationId, check parent folder permissions
        if (record.rootDir === "team" && teamDbRef) {
            try {
                const parentFolderDoc = await getDoc(doc(teamDbRef, record.locationId));
                
                // If parent folder doesn't exist, don't show the record
                if (!parentFolderDoc.exists()) {
                    return false;
                }
                
                const parentFolder = parentFolderDoc.data();
                return canUserAccessFolder(parentFolder);
            } catch (error) {
                console.error("Error checking parent folder permissions:", error);
                return false;
            }
        }
        
        return true;
    }

    if (recent) {
        recent = false;
        explorerState.context = "load";
        explorerState.rootDir = "recent";
        getRecentRecords();
    }

    async function getRecentRecords(){
        try {
            explorerState.loading = true;
            operationLoading = true;
            explorerState.records = [];
            explorerState.selectedRecords = [];
            explorerState.rootDir = "recent";

            // console.log("Getting Recent Records");
            let recentRecords = [];
            
            // Personal storage - fetch all record types including folders
            let personalSnapshot = await getDocs(query(homeDbRef,
                orderBy("updatedOn", "desc"),
                limit(100)));
                
            personalSnapshot.forEach((doc) => {
                recentRecords.push({...(doc.data()), rootDir: "personal"});
            });
            
            // Team storage (if available) - fetch all record types including folders
            if (teamDbRef) {
                let teamSnapshot = await getDocs(query(teamDbRef,
                    orderBy("updatedOn", "desc"),
                    limit(100)));
                    
                teamSnapshot.forEach((doc) => {
                    recentRecords.push({...(doc.data()), rootDir: "team"});
                });
            }
            
            // console.log("Before sorting:", recentRecords.map(r => ({ name: r.name, type: r.type })));
            
            // First, separate folders from other records
            const folders = recentRecords.filter(record => record.type === "folder");
            const nonFolders = recentRecords.filter(record => record.type !== "folder");
            
            // Sort each group by updatedOn timestamp
            folders.sort((a, b) => {
                // Convert to milliseconds for comparison
                const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                return timeB - timeA; // Newest first
            });
            
            nonFolders.sort((a, b) => {
                // Convert to milliseconds for comparison
                const timeA = a.updatedOn ? a.updatedOn.toMillis() : 0;
                const timeB = b.updatedOn ? b.updatedOn.toMillis() : 0;
                return timeB - timeA; // Newest first
            });
            
            // Combine: folders first, then other records
            // Filter team folders by permissions
            const filteredFolders = folders.filter(folder => {
                // If personal folder, keep it
                if (folder.rootDir === "personal") {
                    return true;
                }
                // If team folder, check permissions
                else if (folder.rootDir === "team") {
                    return canUserAccessFolder(folder);
                }
                return false;
            });
            
            // Filter non-folders (projects) by checking parent folder permissions
            const filteredNonFolders = [];
            for (const record of nonFolders) {
                if (await canUserAccessRecord(record)) {
                    filteredNonFolders.push(record);
                }
            }

            explorerState.records = [...filteredFolders, ...filteredNonFolders].slice(0, 100);
            
            // console.log("After sorting - folders:", folders.length, "non-folders:", nonFolders.length);
            // console.log("----RECENT RECORDS (WITH FOLDERS FIRST)----");
            // console.log(explorerState.records);

        } catch(err){
            console.error(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        } finally {
            operationLoading = false;
            explorerState.loading = false;
        }   
    }
</script>
<div class="modal {modalState.selected === 'storageExplorer' || modalState.selected === 'recentProjects' ? 'show d-block' : ''}" role="dialog" tabindex="-1" data-bs-backdrop="static" id="modal-1">
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title text-capitalize">{explorerState.context} {m.project()}</h4>
                <button 
                    type="button"
                    class="btn-close"
                    aria-label={m.close_dialog()}
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                <div class="container-fluid">
                    <div class="row">
                        <NavSidebar />
                        <Workspace />
                        <InfoSidebar />
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>